import { Box, Button, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useScreen from "../../hooks/useScreen";
import useSession from "../../hooks/useSession";
import { SelfieItemProps } from "../../interfaces/SelfieItemProps";
import { State } from "../../interfaces/State";
import { Channel } from "../../interfaces/tracking";
import type { AppDispatch } from "../../reducers/configureStore";
import { setSelectedTransfer } from "../../reducers/style/styleSlice";
import { fetchGetSelfie } from "../../services/selfie";
import { Analytics, EventNames, PhotoSource } from "../../utils/tracking";
import { SelfieSelectorGrid } from "./SelfieSelectorGrid";

interface selfieSelectorProps {
  screen: string;
}

export const SelfieSelector = (props: selfieSelectorProps) => {
  const { screen } = props;
  const [selfieSelected, setSelfieSelected] = useState<SelfieItemProps>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { allSelfies } = useSelector((state: State) => state.selfie);
  const { currentUser, isLoggedIn } = useSession();
  const { isMobile } = useScreen();

  useEffect(() => {
    if (currentUser?.loginStatus) {
      dispatch(
        fetchGetSelfie({ gti: currentUser?.gti, atgId: currentUser?.atgId })
      );
    }
  }, []);

  useEffect(() => {
    const selfieSelected: SelfieItemProps | undefined = allSelfies?.find(
      (selfie: SelfieItemProps) => selfie.selected
    );
    setSelfieSelected(selfieSelected);
  }, [allSelfies]);

  const onUsePhoto = () => {
    dispatch(setSelectedTransfer(undefined));
    const selfieSelected: SelfieItemProps | undefined = allSelfies?.find(
      (selfie: SelfieItemProps) => selfie.selected
    );
    const selfiePosition = allSelfies?.indexOf(selfieSelected);
    Analytics.event(Channel.GA, EventNames.PHTO_SELECTED, {
      device: isMobile ? "mobile" : "desktop",
      option: screen,
      login: isLoggedIn(),
      position: selfiePosition,
    });

    const redirectTo: string =
      new URLSearchParams(window.location.search).get("try-on") ?? "";
    if (selfieSelected) {
      Analytics.event(Channel.GA, EventNames.PHOTO_CONFIRMATION_LOADED, {
        device: isMobile ? "mobile" : "desktop",
        login: isLoggedIn(),
        user_id: currentUser?.gti,
        option: redirectTo,
        photo_saved: allSelfies?.length ? "yes" : "no",
        photos: allSelfies?.length,
        source: PhotoSource.SAVED_PHOTO,
      });
      if (redirectTo !== "analysis") {
        sessionStorage.setItem("vtoSelfieId", selfieSelected.id);
      }
      navigate("?page=vto-" + redirectTo);
    }
  };

  if (!currentUser?.loginStatus || !allSelfies?.length) return null;
  return (
    <>
      <Box
        px={{ base: "1.5rem", md: "3.25rem" }}
        py="0.9375rem"
        display="flex"
        flexDirection="column"
        gap="0.9375rem"
        w="full"
      >
        <Text
          color="#000000"
          fontSize={{ base: "0.75rem", md: "1rem" }}
          letterSpacing={{ base: "0.0056rem", md: "0.0075rem" }}
          lineHeight={{ base: "0.9375rem", md: "1.25rem" }}
          w="full"
          textAlign={{ base: "left", md: "center" }}
        >
          Recently Used
        </Text>

        <SelfieSelectorGrid selfies={allSelfies} />
      </Box>

      <Box
        position={{ base: "fixed", md: "relative" }}
        w="full"
        zIndex={1}
        bottom="0"
        py="0.75rem"
        px={{ base: "1.5rem", md: "3.25rem" }}
        border="none"
        backgroundColor="#FFFFFF"
        pb={{ base: "0.75rem", md: "1.875rem" }}
      >
        <Button
          isDisabled={!selfieSelected}
          fontWeight={500}
          fontSize={{ base: "0.75rem", md: "0.7087rem" }}
          letterSpacing={{ base: "0.0694rem", md: "0.0656rem" }}
          lineHeight={{ base: "1.125rem", md: "1.0125rem" }}
          color="#FFFFFF"
          bg="#000000"
          _hover={{ bg: "#000000" }}
          w="full"
          onClick={onUsePhoto}
        >
          Use photo
        </Button>
      </Box>
    </>
  );
};
